/**
 * Utility functions for button text rotation
 * Refactored to use Promise-based transitions
 */

// Track the history of displayed texts
let textHistory = [window.buttonTextsHighPriority[0]]; // Start with the default text
let rotationInterval;
let rotationTimeout;

/**
 * Initialize button text rotation
 * @param {number} interval - Interval in milliseconds between text rotations
 */
function initButtonTextRotation(interval = 5000) {
  // Reset text history
  textHistory = [window.buttonTextsHighPriority[0]];
  
  // Get the button text element
  const buttonText = document.getElementById('buttonText');
  if (!buttonText) return;
  
  // Make sure the button text is set to the first option without transition
  buttonText.classList.remove('button-text-hidden');
  buttonText.textContent = window.buttonTextsHighPriority[0] + ' →';
  
  // Add the animation class for future transitions (not the initial state)
  buttonText.classList.add('button-text-animation');
  
  // Clear and restart text rotation interval
  if (rotationInterval) {
    clearInterval(rotationInterval);
  }
  
  // Clear any pending rotation timeout
  if (rotationTimeout) {
    clearTimeout(rotationTimeout);
  }
  
  rotationInterval = setInterval(rotateButtonText, interval);
}

/**
 * Function to rotate button text
 * @returns {Promise} - Promise that resolves when text rotation is complete
 */
async function rotateButtonText() {
  const buttonText = document.getElementById('buttonText');
  if (!buttonText) return Promise.resolve();
  
  const currentText = buttonText.textContent;
  
  // Determine which list to pick from (75% high priority, 25% low priority)
  const useHighPriority = Math.random() < 0.75;
  const sourceList = useHighPriority ? window.buttonTextsHighPriority : window.buttonTextsLowPriority;

  // Get available texts from the chosen list (not current, not the previous one)
  let availableTexts = sourceList.filter(text => {
    // If we have at least 2 items in history, exclude both current and previous text
    if (textHistory.length >= 2) {
      return text !== currentText && text !== textHistory[textHistory.length - 2];
    }
    // Otherwise just exclude the current text
    return text !== currentText;
  });

  // If no available texts in chosen list, fall back to the other list
  if (availableTexts.length === 0) {
    const fallbackList = useHighPriority ? window.buttonTextsLowPriority : window.buttonTextsHighPriority;
    availableTexts = fallbackList.filter(text => {
      if (textHistory.length >= 2) {
        return text !== currentText && text !== textHistory[textHistory.length - 2];
      }
      return text !== currentText;
    });
  }

  // Pick a random text from available options
  let randomIndex = Math.floor(Math.random() * availableTexts.length);
  let nextText = availableTexts[randomIndex];
  
  try {
    // Simple fade out with CSS transitions using transitionPromise
    await window.asyncUtils.transitionPromise(
      buttonText,
      (el) => el.classList.add('button-text-hidden'),
      300  // Match to transition duration
    );
    
    // Change text while hidden and add arrow
    buttonText.textContent = nextText + ' →';
    
    // Update history
    textHistory.push(nextText);
    // Keep history at a reasonable size
    if (textHistory.length > 5) textHistory.shift();
    
    // Fade back in using transitionPromise
    await window.asyncUtils.transitionPromise(
      buttonText,
      (el) => el.classList.remove('button-text-hidden'),
      300  // Match to transition duration
    );
    
    return Promise.resolve();
  } catch (error) {
    console.error('Error during button text rotation:', error);
    return Promise.reject(error);
  }
}

/**
 * Start loading state on the button
 * @returns {Promise} - Promise that resolves when loading state is applied
 */
async function setButtonLoadingState() {
  const button = document.getElementById('generateButton');
  const buttonText = document.getElementById('buttonText');
  const loadingSpinner = document.getElementById('loadingSpinner');
  
  if (!button || !buttonText || !loadingSpinner) return Promise.resolve();
  
  // Clear the text rotation interval
  if (rotationInterval) {
    clearInterval(rotationInterval);
    rotationInterval = null;
  }
  
  // Clear any pending rotation timeout
  if (rotationTimeout) {
    clearTimeout(rotationTimeout);
    rotationTimeout = null;
  }
  
  // Disable button immediately to prevent multiple clicks
  button.disabled = true;
  
  // Add opacity
  button.classList.add('opacity-75');
  
  try {
    // Fade out current text with CSS transition
    await window.asyncUtils.transitionPromise(
      buttonText,
      (el) => el.classList.add('button-text-hidden'),
      300  // Match to transition duration
    );
    
    // Change text to loading
    buttonText.textContent = 'Generating...';
    
    // Show the loading spinner
    loadingSpinner.classList.remove('hidden');
    
    // Fade text back in
    await window.asyncUtils.transitionPromise(
      buttonText,
      (el) => el.classList.remove('button-text-hidden'),
      300  // Match to transition duration
    );
    
    return Promise.resolve();
  } catch (error) {
    console.error('Error setting button loading state:', error);
    
    // Apply changes immediately as fallback
    buttonText.textContent = 'Generating...';
    loadingSpinner.classList.remove('hidden');
    buttonText.classList.remove('button-text-hidden');
    
    return Promise.resolve();
  }
}

/**
 * Reset button to normal state
 * @returns {Promise} - Promise that resolves when button is reset
 */
async function resetButtonState() {
  const button = document.getElementById('generateButton');
  const buttonText = document.getElementById('buttonText');
  const loadingSpinner = document.getElementById('loadingSpinner');
  
  if (!button || !buttonText || !loadingSpinner) return Promise.resolve();
  
  // Only fade if animation class is present (indicating we're in the middle of rotating)
  if (buttonText.classList.contains('button-text-animation')) {
    try {
      // Fade out the current text
      await window.asyncUtils.transitionPromise(
        buttonText,
        (el) => el.classList.add('button-text-hidden'),
        300  // Match to transition duration
      );
      
      // Hide loading spinner immediately
      loadingSpinner.classList.add('hidden');
      
      // Reset button state to first option (with arrow)
      buttonText.textContent = window.buttonTextsHighPriority[0] + ' →';
      
      // Remove opacity
      button.classList.remove('opacity-75');
      
      // Re-enable button
      button.disabled = false;
      
      // Fade text back in
      await window.asyncUtils.transitionPromise(
        buttonText,
        (el) => el.classList.remove('button-text-hidden'),
        300  // Match to transition duration
      );
      
      // Restart the button text rotation
      initButtonTextRotation();
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error resetting button state:', error);
      
      // Apply changes immediately as fallback (with arrow)
      buttonText.textContent = window.buttonTextsHighPriority[0] + ' →';
      loadingSpinner.classList.add('hidden');
      button.classList.remove('opacity-75');
      button.disabled = false;
      buttonText.classList.remove('button-text-hidden');
      
      // Restart the button text rotation
      initButtonTextRotation();
      
      return Promise.resolve();
    }
  } else {
    // Immediately reset without transition (with arrow)
    buttonText.textContent = window.buttonTextsHighPriority[0] + ' →';
    loadingSpinner.classList.add('hidden');
    button.classList.remove('opacity-75');
    button.disabled = false;
    
    // Restart the button text rotation
    initButtonTextRotation();
    
    return Promise.resolve();
  }
}

/**
 * Stop button text rotation without changing button state
 */
function stopButtonTextRotation() {
  if (rotationInterval) {
    clearInterval(rotationInterval);
    rotationInterval = null;
  }

  if (rotationTimeout) {
    clearTimeout(rotationTimeout);
    rotationTimeout = null;
  }
}

// Export functions
window.buttonUtils = {
  initButtonTextRotation,
  rotateButtonText,
  setButtonLoadingState,
  resetButtonState,
  stopButtonTextRotation
};